// AccordionBlock.tsx
import React, { useState, useEffect, useCallback } from "react";
import { StoryblokBlockComponent } from "../../lib/storyblok/types/StoryblokComponent";
import AccordionUi from "../../ui/Accordion";
import { AccordionBlock } from "../../lib/storyblok/types/blocks/AccordionBlock";
import Button from "../../ui/Button";
import { useLinks } from "../../lib/storyblok/useLinks";
import { FaqManager } from "../../lib/utils/schema/FaqManager";
import { useContentStyle, useButtonStyle } from "../../hooks/useStyle";

const Accordion: StoryblokBlockComponent<AccordionBlock> = ({
  title,
  description,
  buttons,
  innerSpacing,
  outerSpacing,
  width,
  style,
  preventExpandFirstItem = false,
  items,
}) => {
  const isInverted = useContentStyle(style);
  const resolveLink = useLinks();
  const { buttonVariant } = useButtonStyle(style);
  const [expanded, setExpanded] = useState<string | false>(
    preventExpandFirstItem ? false : "panel0"
  );

  const buttonCollection = buttons?.map((button) => (
    <Button
      key={button._uid}
      variant={button.buttonType || buttonVariant || "default"}
      href={button.buttonLink ? resolveLink(button.buttonLink) : ""}
    >
      {button.buttonText}
    </Button>
  ));

  const handleChange = useCallback(
    (panel: string) => () => {
      setExpanded(expanded === panel ? false : panel);
    },
    [expanded]
  );

  const handleKeyboardNavigation = useCallback((index: number) => {
    const button = document.getElementById(`panel${index}-header`);
    if (button) {
      button.focus();
    }
    setExpanded(`panel${index}`);
  }, []);

  useEffect(() => {
    if (items) {
      FaqManager.addItems(items);
      return () => {
        FaqManager.removeItems(items);
      };
    }
  }, [items]);

  return (
    <AccordionUi
      title={title}
      description={description}
      buttons={buttonCollection}
      innerSpacing={innerSpacing}
      outerSpacing={outerSpacing}
      style={style}
      width={width}
      expanded={expanded}
      handleChange={handleChange}
      inverted={isInverted}
    >
      {items?.map((item, index) => (
        <AccordionUi.Item
          title={item.title}
          description={item.description}
          key={item._uid}
          index={index}
          expanded={expanded === `panel${index}`}
          onChange={handleChange(`panel${index}`)}
          totalItems={items.length}
          onKeyboardNavigation={handleKeyboardNavigation}
          inverted={isInverted}
        />
      ))}
    </AccordionUi>
  );
};

export default Accordion;
